import { Checkbox } from "../../../../components/Checkbox";
import { TextInput } from "../../../../components/TextInput";
import { Button } from "../../../../components/Button";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  CalendarEventResponse,
  CalendarEventType,
  CreateCalendarEventRequest,
  OrganizationMember,
  RecurrenceEditScope,
  RecurrenceRule,
} from "../../../../types/api";
import {
  createEvent,
  updateEvent,
  deleteEvent,
  getOrganizationMembers,
  searchLocations,
  getLocationDetails,
  directionsUrl,
  listAttachments,
  uploadAttachment,
  deleteAttachment,
  type LocationSuggestion,
  type CalendarEventAttachment,
} from "../../../../services/calendar";
import { useAuth } from "../../../../hooks/useAuth";
import {
  browserTimeZone,
  utcDateInputValue,
  exclusiveEndToInclusiveDate,
  utcToZonedInput,
  zonedInputToUtcIso,
} from "../../../../utils/calendarTime";
import {
  DatePickerField,
  TimeSelectField,
  TimezoneSelectField,
  CoachSelectField,
} from "./ScheduleFields";
import { getErrorMessage } from "../../../../utils/errorHandler";
import { RecurrenceFields } from "./RecurrenceFields";
import {
  RecurrenceScopeModal,
  type RecurrenceScopeAction,
} from "./RecurrenceScopeModal";
import { DEFAULT_RECURRENCE, recurrenceRequest, validateRecurrence } from "./recurrenceRule";
import { ScheduleIngestionModal } from "./ScheduleIngestionModal";
import { TournamentGamesPanel } from "./TournamentGamesPanel";
import { eventTypeLabelWithEmoji } from "./eventTypeLabel";
import { eventGameCoaches, gameCoachesNote } from "./gameCoaches";
import "./ScheduleSection.css";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Labels come from the shared eventTypeLabel helper ("Tournament" shows as "Event"); the enum
// VALUES are unchanged and still drive all logic below. Tournaments are built only via the
// itinerary builder, so the quick editor offers just the personal event types.
const QUICK_TYPES: readonly CalendarEventType[] = ["RecruitVisit", "OneOff"];

// A short, US-centric timezone list for Slice 1. Location-derived timezones arrive in Slice 3.
const TIMEZONE_OPTIONS = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

interface EventEditorPanelProps {
  /** The event being edited, or null to create a new one. */
  event: CalendarEventResponse | null;
  /** For create mode: the day the user clicked (YYYY-MM-DD), used to seed the dates. */
  defaultDate?: string;
  /** When opened from the "Set location" prompt, focus the location field on mount. */
  focusLocation?: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
}

export const EventEditorPanel = ({
  event,
  defaultDate,
  focusLocation = false,
  onClose,
  onSaved,
  onDeleted,
}: EventEditorPanelProps) => {
  const isEdit = event !== null;
  // Rebuilding replaces an existing tournament's games; for a new/non-tournament event the same
  // modal just auto-fills (creates) instead.
  const isRebuild = isEdit && event?.eventType === "Tournament";
  const { user } = useAuth();
  const currentUserId = user?.profile?.id;

  const defaultTz = useMemo(() => {
    const btz = browserTimeZone();
    return TIMEZONE_OPTIONS.includes(btz) ? btz : "America/New_York";
  }, []);

  const [title, setTitle] = useState("");
  const [eventType, setEventType] = useState<CalendarEventType>("RecruitVisit");

  // Attachment copy follows the event type: "itinerary" reads as tournament jargon on a recruit
  // visit, where the PDF is a campus schedule, or on a camp, where it is a waiver or facility map.
  const attachmentLabel =
    eventType === "Tournament" ? "Itinerary attachments" : "Attachments";
  const attachmentUploadLabel =
    eventType === "Tournament"
      ? "⬆ Upload PDF (schedule, bracket, itinerary)"
      : "⬆ Upload PDF";

  // Quick editor offers Recruit visit + One-off. Tournaments are created only through the
  // itinerary builder, so Tournament shows as an option only when editing one that already exists.
  const typeOptions = useMemo(() => {
    const values: CalendarEventType[] =
      event?.eventType === "Tournament" ? ["Tournament", ...QUICK_TYPES] : [...QUICK_TYPES];
    return values.map((value) => ({ value, label: eventTypeLabelWithEmoji(value) }));
  }, [event]);
  const [isAllDay, setIsAllDay] = useState(true);
  const [timeZone, setTimeZone] = useState(defaultTz);
  // Dates (YYYY-MM-DD). For all-day, end is the inclusive last day. For timed, the day part.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  // Times (HH:mm in the selected timezone) — timed events only.
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [notes, setNotes] = useState("");
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  // Read-only: coaches on some games but not the event, so editing the event's coaches doesn't
  // hide them. Anyone just picked as an event coach drops out of the note.
  const gameCoachNote = event
    ? gameCoachesNote(eventGameCoaches(event).filter((c) => !assigneeIds.includes(c.userId)))
    : null;
  // Location (Google Places via backend proxy).
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState<string | null>(null);
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  const [locationPlaceId, setLocationPlaceId] = useState<string | null>(null);
  const [locationQuery, setLocationQuery] = useState("");
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [itineraryUrl, setItineraryUrl] = useState("");
  const [showIngestionModal, setShowIngestionModal] = useState(false);
  const [attachments, setAttachments] = useState<CalendarEventAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // Repeat is a CREATE-time choice: the server materializes the occurrences up front, so there is
  // no rule left to edit afterwards, only occurrences to scope a change across.
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceRule>(DEFAULT_RECURRENCE);
  // Non-null while the "which occurrences?" prompt is up. Only ever set for a series occurrence,
  // which is what keeps a standalone event's save/delete free of an extra step.
  const [scopePrompt, setScopePrompt] = useState<RecurrenceScopeAction | null>(null);
  // The already-validated payload waiting on the scope answer. A ref, not state: it is handed
  // straight to the confirm handler and never rendered.
  const pendingRequestRef = useRef<CreateCalendarEventRequest | null>(null);

  /** This event is one occurrence of a repeating series, so writes need a scope. */
  const isSeries = isEdit && event?.recurrenceId != null;

  // Load attachments when editing an existing event.
  useEffect(() => {
    if (!event) {
      setAttachments([]);
      return;
    }
    let active = true;
    listAttachments(event.id)
      .then((a) => {
        if (active) setAttachments(a);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      active = false;
    };
  }, [event]);

  // Load the org's members once for the assignee picker.
  useEffect(() => {
    let active = true;
    getOrganizationMembers()
      .then((m) => {
        if (active) setMembers(m);
      })
      .catch(() => {
        /* non-fatal: the picker just shows no options */
      });
    return () => {
      active = false;
    };
  }, []);

  // Seed the form whenever the target event (or the create default date) changes.
  useEffect(() => {
    if (event) {
      const tz = event.timeZone || defaultTz;
      setTitle(event.title);
      setEventType(event.eventType);
      setIsAllDay(event.isAllDay);
      setTimeZone(TIMEZONE_OPTIONS.includes(tz) ? tz : defaultTz);
      if (event.isAllDay) {
        setStartDate(utcDateInputValue(event.startsAtUtc));
        setEndDate(exclusiveEndToInclusiveDate(event.endsAtUtc));
      } else {
        // utcToZonedInput returns "YYYY-MM-DDTHH:mm" in the event tz — split into date + time.
        const [sd, st] = utcToZonedInput(event.startsAtUtc, tz).split("T");
        const [ed, et] = utcToZonedInput(event.endsAtUtc, tz).split("T");
        setStartDate(sd);
        setStartTime(st || "09:00");
        setEndDate(ed);
        setEndTime(et || "10:00");
      }
      setNotes(event.notes ?? "");
      setAssigneeIds(event.assignees.map((a) => a.userId));
      setLocationName(event.locationName ?? "");
      setLocationAddress(event.locationAddress);
      setLocationLat(event.locationLat);
      setLocationLng(event.locationLng);
      setLocationPlaceId(event.locationPlaceId);
      setLocationQuery(event.locationName ?? "");
      setItineraryUrl(event.itineraryUrl ?? "");
    } else {
      const day = defaultDate || new Date().toISOString().slice(0, 10);
      setTitle("");
      setEventType("RecruitVisit");
      setIsAllDay(true);
      setTimeZone(defaultTz);
      setStartDate(day);
      setEndDate(day);
      setStartTime("09:00");
      setEndTime("10:00");
      setNotes("");
      // New quick events default to a personal type, so seed the creator as an assignee.
      setAssigneeIds(currentUserId ? [currentUserId] : []);
      setLocationName("");
      setLocationAddress(null);
      setLocationLat(null);
      setLocationLng(null);
      setLocationPlaceId(null);
      setLocationQuery("");
      setItineraryUrl("");
    }
    // A fresh target event means a fresh repeat choice; never carry one form's series over.
    setRepeatEnabled(false);
    setRecurrence(DEFAULT_RECURRENCE);
    setScopePrompt(null);
    pendingRequestRef.current = null;
    setSuggestions([]);
    setShowSuggestions(false);
    setError("");
  }, [event, defaultDate, defaultTz, currentUserId]);

  // A recruit visit or one-off is almost always the caller's own to-do, so when creating one seed
  // them as an assignee. Tournaments stay org-wide (unassigned) by default. Only on create, and
  // only additive, so the user can still remove themselves or add others.
  const handleTypeChange = (value: CalendarEventType) => {
    setEventType(value);
    if (
      !isEdit &&
      currentUserId &&
      (value === "RecruitVisit" || value === "OneOff")
    ) {
      setAssigneeIds((prev) => (prev.includes(currentUserId) ? prev : [...prev, currentUserId]));
    }
  };

  // Opened from the "Set location" prompt: jump to + focus the location field.
  useEffect(() => {
    if (!focusLocation) return;
    const el = document.getElementById("event-location");
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    (el as HTMLInputElement).focus();
  }, [focusLocation]);

  // Debounced location autocomplete.
  useEffect(() => {
    if (!showSuggestions || locationQuery.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const handle = setTimeout(() => {
      searchLocations(locationQuery.trim())
        .then(setSuggestions)
        .catch(() => setSuggestions([]));
    }, 300);
    return () => clearTimeout(handle);
  }, [locationQuery, showSuggestions]);

  const handleLocationInput = (value: string) => {
    setLocationQuery(value);
    setShowSuggestions(true);
    // Typing is treated as free-text until a suggestion is picked; clear any resolved place.
    setLocationName(value);
    setLocationAddress(null);
    setLocationLat(null);
    setLocationLng(null);
    setLocationPlaceId(null);
  };

  const selectPlace = async (s: LocationSuggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);
    setLocationQuery(s.description);
    setLocationName(s.description);
    try {
      const details = await getLocationDetails(s.placeId);
      setLocationAddress(details.formattedAddress || null);
      setLocationLat(details.lat);
      setLocationLng(details.lng);
      setLocationPlaceId(details.placeId);
      if (details.timeZone) {
        setTimeZone(details.timeZone);
      }
    } catch {
      // Keep the free-text name if details resolution fails.
    }
  };

  /**
   * The repeat rule to send, or null when the coach asked for one the server would reject. Returns
   * an empty object (not null) for every non-repeating case, so callers can spread it blindly.
   */
  const recurrencePayload = (): { recurrence?: RecurrenceRule } | null => {
    if (isEdit || !repeatEnabled) return {};
    // `startDate` is the event's first day in both all-day and timed modes, and is the only thing
    // the repeat end date can be checked against.
    const invalid = validateRecurrence(recurrence, startDate);
    if (invalid) {
      setError(invalid);
      return null;
    }
    // Weekly carries its weekdays with the start day included; other frequencies drop them.
    return { recurrence: recurrenceRequest(recurrence, startDate) };
  };

  const buildRequest = (): CreateCalendarEventRequest | null => {
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      setError("Give the event a title.");
      return null;
    }

    const repeat = recurrencePayload();
    if (!repeat) return null;

    if (isAllDay) {
      if (!startDate || !endDate) {
        setError("Pick a start and end date.");
        return null;
      }
      if (endDate < startDate) {
        setError("The end date can't be before the start date.");
        return null;
      }
      const startsAtUtc = `${startDate}T00:00:00.000Z`;
      // Store an exclusive end: midnight UTC of the day after the inclusive last day.
      const exclusive = new Date(new Date(`${endDate}T00:00:00Z`).getTime() + MS_PER_DAY);
      return {
        title: trimmed,
        eventType,
        startsAtUtc,
        endsAtUtc: exclusive.toISOString(),
        isAllDay: true,
        timeZone,
        notes: notes.trim() || null,
        assigneeUserIds: assigneeIds,
        ...locationPayload(),
        ...repeat,
      };
    }

    if (!startDate || !endDate || !startTime || !endTime) {
      setError("Pick a start and end date/time.");
      return null;
    }
    const startsAtUtc = zonedInputToUtcIso(`${startDate}T${startTime}`, timeZone);
    const endsAtUtc = zonedInputToUtcIso(`${endDate}T${endTime}`, timeZone);
    if (new Date(endsAtUtc) < new Date(startsAtUtc)) {
      setError("The end time can't be before the start time.");
      return null;
    }
    return {
      title: trimmed,
      eventType,
      startsAtUtc,
      endsAtUtc,
      isAllDay: false,
      timeZone,
      notes: notes.trim() || null,
      assigneeUserIds: assigneeIds,
      ...locationPayload(),
      ...repeat,
    };
  };

  const locationPayload = () => ({
    locationName: locationName.trim() || null,
    locationAddress,
    locationLat,
    locationLng,
    locationPlaceId,
    itineraryUrl: itineraryUrl.trim() || null,
  });

  const handleUpload = async (file: File | undefined) => {
    if (!file || !event) return;
    if (file.type !== "application/pdf") {
      setError("Only PDF files can be attached.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const created = await uploadAttachment(event.id, file);
      setAttachments((prev) => [...prev, created]);
    } catch {
      setError("Couldn't upload the file. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!event) return;
    try {
      await deleteAttachment(event.id, attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch {
      setError("Couldn't remove the file. Try again.");
    }
  };

  /** Write the event. `scope` is passed only for an occurrence of a repeating series. */
  const persist = async (request: CreateCalendarEventRequest, scope?: RecurrenceEditScope) => {
    setSaving(true);
    setError("");
    try {
      if (isEdit && event) {
        await updateEvent(event.id, scope ? { ...request, scope } : request);
      } else {
        await createEvent(request);
      }
      onSaved();
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't save the event. Try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const request = buildRequest();
    if (!request) return;
    // A series occurrence asks which occurrences first; anything else saves straight through, so
    // a standalone event's flow is byte-for-byte what it always was.
    if (isSeries) {
      pendingRequestRef.current = request;
      setScopePrompt("save");
      return;
    }
    await persist(request);
  };

  const performDelete = async (scope?: RecurrenceEditScope) => {
    if (!event) return;
    setSaving(true);
    setError("");
    try {
      await deleteEvent(event.id, scope);
      onDeleted();
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't delete the event. Try again."));
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    // The scope prompt already asks for confirmation (and is destructive-by-choice), so a series
    // occurrence gets that instead of the plain confirm, not as well as it.
    if (isSeries) {
      setScopePrompt("delete");
      return;
    }
    if (!window.confirm("Delete this event? This can't be undone.")) return;
    await performDelete();
  };

  const handleScopeConfirm = (scope: RecurrenceEditScope) => {
    const action = scopePrompt;
    setScopePrompt(null);
    if (action === "delete") {
      void performDelete(scope);
      return;
    }
    const request = pendingRequestRef.current;
    pendingRequestRef.current = null;
    if (request) void persist(request, scope);
  };

  const handleScopeCancel = () => {
    setScopePrompt(null);
    pendingRequestRef.current = null;
  };

  return (
    <>
    <aside className="event-editor">
      <div className="event-editor__head">
        <div className="event-editor__title">{isEdit ? "Edit event" : "New event"}</div>
        <ModalCloseButton onClick={onClose} />
      </div>

      <div className="event-editor__body">
        <div className="event-editor__field">
          <span id="event-editor-panel-event-type-0">Event type</span>
          <div className="event-editor__types" role="group" aria-labelledby="event-editor-panel-event-type-0">
            {typeOptions.map((opt) => {
              const selected = eventType === opt.value;
              const selClass = selected
                ? ` event-editor__type--sel-${opt.value.toLowerCase()}`
                : "";
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`event-editor__type${selClass}`}
                  onClick={() => handleTypeChange(opt.value)}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/*
            `TextInput`, not a raw <input>. `.event-editor__field textarea` is deliberately
            textarea-only — a descendant `input` selector would double-border the primitive — which
            left every raw <input> in this panel with NO styling at all: no border, no padding, no
            radius. That is what QA saw on the Quick Event title.

            The hand-written <label> stays rather than moving to TextInput's own `label` prop: the
            sibling fields here (Starts, Ends, Timezone, Notes) label themselves at 12px/700 grey
            via `.event-editor__field label`, and letting the primitive draw this one would make the
            three text fields the only ones in the panel with a different label.
        */}
        <div className="event-editor__field">
          <label htmlFor="event-title">Title</label>
          <TextInput
            id="event-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. PBR National Championship"
            maxLength={200}
          />
        </div>

        <Checkbox
        label="All-day / multi-day event"
        checked={isAllDay}
        onChange={(e) => setIsAllDay(e.target.checked)}
      />

        {isAllDay ? (
          <div className="event-editor__row">
            <div className="event-editor__field">
              <label htmlFor="event-start-date">Starts</label>
              <DatePickerField id="event-start-date" value={startDate} onChange={setStartDate} />
            </div>
            <div className="event-editor__field">
              <label htmlFor="event-end-date">Ends</label>
              <DatePickerField id="event-end-date" value={endDate} onChange={setEndDate} align="right" />
            </div>
          </div>
        ) : (
          <>
            <div className="event-editor__row">
              <div className="event-editor__field">
                <label htmlFor="event-editor-panel-starts-0">Starts</label>
                <DatePickerField id="event-editor-panel-starts-0" value={startDate} onChange={setStartDate} />
              </div>
              <div className="event-editor__field event-editor__field--time">
                <label>&nbsp;</label>
                <TimeSelectField value={startTime} onChange={setStartTime} />
              </div>
            </div>
            <div className="event-editor__row">
              <div className="event-editor__field">
                <label htmlFor="event-editor-panel-ends-1">Ends</label>
                <DatePickerField id="event-editor-panel-ends-1" value={endDate} onChange={setEndDate} />
              </div>
              <div className="event-editor__field event-editor__field--time">
                <label>&nbsp;</label>
                <TimeSelectField value={endTime} onChange={setEndTime} />
              </div>
            </div>
            <div className="event-editor__field">
              <label>Timezone</label>
              <TimezoneSelectField value={timeZone} onChange={setTimeZone} />
            </div>
          </>
        )}

        {/* Repeat is offered on create only. Tournaments carry games and ingestion state that are
            not series-shaped, and the server rejects a repeating one outright. */}
        {!isEdit && eventType !== "Tournament" && (
          <RecurrenceFields
            enabled={repeatEnabled}
            onEnabledChange={setRepeatEnabled}
            value={recurrence}
            onChange={setRecurrence}
            firstDayKey={startDate}
          />
        )}

        {isSeries && (
          <div className="event-editor__hint" data-testid="event-editor-series-note">
            This event repeats. When you save or delete, we'll ask which occurrences it applies to.
          </div>
        )}

        <div className="event-editor__field">
          <label htmlFor="event-location">Location</label>
          <div className="location-typeahead">
            <TextInput
              id="event-location"
              value={locationQuery}
              onChange={(e) => handleLocationInput(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Search a venue or address"
              autoComplete="off"
            />
            {showSuggestions && suggestions.length > 0 && (
              <div className="location-results">
                {suggestions.map((s) => (
                  <button
                    type="button"
                    key={s.placeId}
                    className="location-result"
                    onClick={() => selectPlace(s)}
                  >
                    {s.description}
                  </button>
                ))}
              </div>
            )}
          </div>
          {locationAddress && (
            <div className="location-resolved">
              <div className="location-resolved__addr">📍 {locationAddress}</div>
              <div className="location-resolved__chips">
                {!isAllDay && <span className="location-chip location-chip--tz">🕑 {timeZone}</span>}
                {(() => {
                  const dir = directionsUrl({
                    locationPlaceId,
                    locationLat,
                    locationLng,
                    locationAddress,
                    locationName,
                  });
                  return dir ? (
                    <a className="location-link" href={dir} target="_blank" rel="noopener noreferrer">
                      ↗ Directions
                    </a>
                  ) : null;
                })()}
              </div>
            </div>
          )}
          <div className="event-editor__hint">
            Search is powered by Google Places. Pick a place to fill in the address, timezone, and
            directions. You can also just type a location.
          </div>
        </div>

        <div className="event-editor__field">
          <label>
            Assigned to <span className="event-editor__optional">(optional)</span>
          </label>
          <CoachSelectField
            members={members}
            value={assigneeIds}
            onChange={setAssigneeIds}
            currentUserId={currentUserId}
          />
          <div className="event-editor__hint">
            Everyone in the org sees this event. Assignees are just the coaches responsible for it.
          </div>
          {gameCoachNote && (
            <div className="event-editor__hint" data-testid="editor-game-coaches">
              {gameCoachNote}
            </div>
          )}
        </div>

        {event && event.eventType === "Tournament" && (
          <TournamentGamesPanel
            calendarEventId={event.id}
            members={members}
            currentUserId={currentUserId}
          />
        )}

        {/* Schedule import / itinerary link only applies to Events (tournaments/showcases). A recruit
            visit or one-off has no schedule to pull in, so hide it for those types. */}
        {eventType === "Tournament" && (
          <div className="event-editor__field">
            <label htmlFor="event-itinerary">Itinerary link</label>
            <TextInput
              id="event-itinerary"
              value={itineraryUrl}
              onChange={(e) => setItineraryUrl(e.target.value)}
              placeholder="https://…/schedule"
            />
            {/^https?:\/\//i.test(itineraryUrl.trim()) && (
              <a
                className="location-link"
                href={itineraryUrl.trim()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="itinerary-open-link"
              >
                ↗ Open link
              </a>
            )}
            <button
              type="button"
              className="event-editor__future"
              onClick={() => setShowIngestionModal(true)}
              data-testid="auto-fill-schedule-button"
            >
              {isRebuild
                ? "🔄 Rebuild schedule from a new link or upload"
                : "✨ Auto-fill schedule from a link or upload"}
            </button>
            <div className="event-editor__hint">
              {isRebuild
                ? "This link is saved and clickable. Rebuild replaces this event's games from a new link, PDF, or screenshot."
                : "The link is saved as a clickable itinerary link. To pull games in now, paste a schedule link or upload a PDF or screenshot."}
            </div>
          </div>
        )}

        {/* PDF attachments apply to EVERY event type, not just tournaments: a recruit visit has a
            campus itinerary, a camp has a waiver or a facility map. The label names the whole group
            of controls rather than a single input, so it is a span + role="group" rather than a
            <label> (see the accessibility notes in CLAUDE.md). */}
        <div className="event-editor__field">
          <span id="event-attachments-label" className="event-editor__group-label">
            {attachmentLabel}
          </span>
          {event ? (
            <div className="attach-list" role="group" aria-labelledby="event-attachments-label">
              {attachments.map((a) => (
                <div key={a.id} className="attach-item">
                  <span className="attach-item__meta">
                    <span className="attach-item__pdf">PDF</span>
                    {a.fileName}
                  </span>
                  <span className="attach-item__actions">
                    <a className="location-link" href={a.downloadUrl} target="_blank" rel="noopener noreferrer">
                      View
                    </a>
                    <button
                      type="button"
                      className="attach-item__rm"
                      onClick={() => handleDeleteAttachment(a.id)}
                    >
                      Remove
                    </button>
                  </span>
                </div>
              ))}
              <label className="attach-upload">
                {uploading ? "Uploading…" : attachmentUploadLabel}
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  disabled={uploading}
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
              </label>
            </div>
          ) : (
            <div className="event-editor__hint">Save the event first, then you can attach PDFs.</div>
          )}
        </div>

        <div className="event-editor__field">
          <label htmlFor="event-notes">Notes</label>
          <textarea
            id="event-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes for the staff"
          />
        </div>


        {error && <div className="event-editor__error">{error}</div>}
      </div>

      <div className="event-editor__foot">
        {isEdit ? (
          <Button variant="secondary"
            className="event-editor__btn--danger"
            onClick={handleDelete}
            disabled={saving}
          >
            Delete
          </Button>
        ) : (
          <span />
        )}
        <div className="event-editor__foot-right">
          <Button variant="secondary"  onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save event"}
          </Button>
        </div>
      </div>
    </aside>
    <RecurrenceScopeModal
      isOpen={scopePrompt !== null}
      action={scopePrompt ?? "save"}
      eventId={event?.id}
      busy={saving}
      onCancel={handleScopeCancel}
      onConfirm={handleScopeConfirm}
    />
    {showIngestionModal && (
      <ScheduleIngestionModal
        onClose={() => setShowIngestionModal(false)}
        defaultTitle={title}
        defaultTimeZone={timeZone}
        defaultSourceUrl={itineraryUrl.trim() || undefined}
        targetCalendarEventId={isRebuild && event ? event.id : undefined}
        onConfirmed={() => {
          // A new tournament (with its games) now exists on the calendar.
          // Refresh the calendar so it shows up; onSaved also closes the editor.
          onSaved();
        }}
      />
    )}
    </>
  );
};
